import { db } from './config';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, query } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

const COLLECTION_NAME = 'adminSettings';
const USERS_COLLECTION = 'adminUsers';
const SETTINGS_DOC_ID = 'app_settings';
const DEFAULT_ADMIN = {
  id: 'admin-1',
  username: 'admin',
  password: 'admin123',
  email: 'lifeandwellnesscenter78@gmail.com',
  name: 'Administrator',
  role: 'admin'
};

// Initialize Firestore with default settings if they don't exist
export const initializeSettings = async () => {
  try {
    // Check if Firestore is initialized
    if (!db) {
      console.error('Firestore not initialized!');
      return false;
    }
    
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC_ID);
    
    try {
      const settingsSnap = await getDoc(settingsRef);
      
      if (!settingsSnap.exists()) {
        // Create default settings
        try {
          await setDoc(settingsRef, {
            theme: 'light',
            notifications: true
          });
        } catch (error) {
          console.error('Error creating default settings:', error.message);
        }
      }
    } catch (error) {
      console.error('Error checking settings:', error.message);
    }
    
    // Check if any admin users exist
    try {
      const usersCollection = collection(db, USERS_COLLECTION);
      const usersQuery = query(usersCollection);
      const usersSnapshot = await getDocs(usersQuery);
      
      if (usersSnapshot.empty) {
        // Create default admin user
        try {
          await setDoc(doc(db, USERS_COLLECTION, DEFAULT_ADMIN.id), DEFAULT_ADMIN);
        } catch (error) {
          console.error('Error creating default admin user:', error.message);
        }
      }
    } catch (error) {
      console.error('Error checking admin users:', error.message);
    }
    
    return true;
  } catch (error) {
    console.error('Error initializing settings:', error);
    return false;
  }
};

// Do not auto-run initialization on import to avoid module load order issues.
// App.js will call initializeSettings() after Firebase config is ready.

// Get all settings
export const getSettings = async () => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC_ID);
    const settingsSnap = await getDoc(settingsRef);
    
    if (settingsSnap.exists()) {
      return settingsSnap.data();
    }
    return {};
  } catch (error) {
    console.error('Error getting settings:', error);
    return {};
  }
};

// Update settings
export const updateSettings = async (settings) => {
  try {
    const settingsRef = doc(db, COLLECTION_NAME, SETTINGS_DOC_ID);
    await updateDoc(settingsRef, settings);
    return settings;
  } catch (error) {
    console.error('Error updating settings:', error);
    throw error;
  }
};

// Get all admin users
export const getAdminUsers = async () => {
  try {
    const usersCollection = collection(db, USERS_COLLECTION);
    const usersSnapshot = await getDocs(usersCollection);
    
    const users = [];
    usersSnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting admin users:', error);
    // If there's an error, try to create and return the default admin
    try {
      await setDoc(doc(db, USERS_COLLECTION, DEFAULT_ADMIN.id), DEFAULT_ADMIN);
      return [DEFAULT_ADMIN];
    } catch (innerError) {
      console.error('Failed to create default admin during error recovery:', innerError);
      return [];
    }
  }
};

// Add a new admin user
export const addAdminUser = async (userData) => {
  try {
    const newUser = {
      id: uuidv4(),
      ...userData,
      role: 'admin'
    };
    
    await setDoc(doc(db, USERS_COLLECTION, newUser.id), newUser);
    return newUser;
  } catch (error) {
    console.error('Error adding admin user:', error);
    throw error;
  }
};

// Update an admin user
export const updateAdminUser = async (id, userData) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, id);
    await updateDoc(userRef, userData);
    
    // Return the updated user
    const updatedUserSnap = await getDoc(userRef);
    return { id, ...updatedUserSnap.data() };
  } catch (error) {
    console.error('Error updating admin user:', error);
    throw error;
  }
};

// Delete an admin user
export const deleteAdminUser = async (id) => {
  try {
    // First check if this is the last admin
    const users = await getAdminUsers();
    if (users.length <= 1) {
      throw new Error('Cannot delete the last admin user');
    }
    
    await deleteDoc(doc(db, USERS_COLLECTION, id));
    return users.filter(user => user.id !== id);
  } catch (error) {
    console.error('Error deleting admin user:', error);
    throw error;
  }
};

// Validate admin credentials
export const validateAdminCredentials = async (username, password) => {
  try {
    // Force create default admin if no users exist
    let users = [];
    try {
      users = await getAdminUsers();
    } catch (error) {
      console.error('Error getting admin users during validation:', error);
    }
    
    if (users.length === 0) {
      try {
        await setDoc(doc(db, USERS_COLLECTION, DEFAULT_ADMIN.id), DEFAULT_ADMIN);
        // Add the default admin to the users array
        users.push(DEFAULT_ADMIN);
      } catch (error) {
        console.error('Error creating default admin during validation:', error);
      }
    }
    
    // Direct check for default admin credentials as fallback
    if (username === DEFAULT_ADMIN.username && password === DEFAULT_ADMIN.password) {
      return DEFAULT_ADMIN;
    }
    
    const user = users.find(user => user.username === username && user.password === password);
    
    return user;
  } catch (error) {
    console.error('Error validating credentials:', error);
    
    // Last resort fallback - if everything fails but credentials match default admin
    if (username === DEFAULT_ADMIN.username && password === DEFAULT_ADMIN.password) {
      return DEFAULT_ADMIN;
    }
    
    return null;
  }
};